"use client"

import { useEffect, useState } from "react"
import { mariaBonitaHorario } from "@/lib/restaurants/maria-bonita"

const DIAS_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

type Estado = { aberto: boolean; texto: string }

/**
 * Estado da casa no momento, no fuso de Lisboa (o telemóvel do cliente pode
 * estar noutro). Calculado só depois de montar: em SSR não há relógio do
 * utilizador e renderizar aqui partiria a hidratação.
 */
function calcularEstado(): Estado {
  const partes = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Lisbon",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date())

  const obter = (t: string) => partes.find((p) => p.type === t)?.value ?? ""
  const dia = DIAS_EN.indexOf(obter("weekday"))
  const minutos = Number(obter("hour")) * 60 + Number(obter("minute"))

  const hoje = mariaBonitaHorario.find((h) => h.dia === dia)
  if (hoje && !hoje.fechado && minutos >= hoje.abre && minutos < hoje.fecha) {
    return { aberto: true, texto: "Aberto agora · até às 00h00" }
  }
  if (hoje && !hoje.fechado && minutos < hoje.abre) {
    return { aberto: false, texto: "Hoje abre às 19h00" }
  }

  // procura o próximo dia com serviço
  for (let i = 1; i <= 7; i++) {
    const prox = mariaBonitaHorario.find((h) => h.dia === (dia + i) % 7)
    if (prox && !prox.fechado) {
      const quando = i === 1 ? "Amanhã" : prox.nome
      return { aberto: false, texto: `${quando} às 19h00` }
    }
  }
  return { aberto: false, texto: "Ter a Sáb · 19h00–00h00" }
}

/** Pastilha "Aberto agora / fechado" com ponto a pulsar. */
export function EstadoAgora({
  fallback,
  ink,
  hairline,
}: {
  fallback: string
  ink: string
  hairline: string
}) {
  const [estado, setEstado] = useState<Estado | null>(null)

  useEffect(() => {
    setEstado(calcularEstado())
    const id = setInterval(() => setEstado(calcularEstado()), 60_000)
    return () => clearInterval(id)
  }, [])

  const aberto = estado?.aberto ?? false
  const texto = estado?.texto ?? fallback

  return (
    <span
      className="bonita-label inline-flex items-center gap-2.5 rounded-full px-4 py-2"
      style={{
        backgroundColor: "rgba(36, 26, 18, 0.06)",
        border: `1.5px solid ${hairline}`,
        color: ink,
        letterSpacing: "0.18em",
      }}
    >
      <span
        aria-hidden
        className={`inline-block h-2 w-2 rounded-full ${aberto ? "bonita-live" : ""}`}
        style={{ backgroundColor: aberto ? "#2E9E4F" : "#C6362E" }}
      />
      {texto}
    </span>
  )
}

function IconeWhatsApp({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden focusable="false">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  )
}

/**
 * Botão flutuante de WhatsApp. Fica em baixo à direita para não chocar com o
 * botão do carrinho, que vive ao centro.
 */
export function BotaoWhatsApp({
  telefone,
  mensagem = "Olá! Vi a ementa online e queria fazer uma encomenda.",
}: {
  telefone: string
  mensagem?: string
}) {
  const numero = telefone.replace(/\D/g, "")
  return (
    <a
      href={`https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Encomendar pelo WhatsApp"
      /* em telemóvel sobe: o botão do carrinho vive ao centro, em baixo */
      className="bonita-zap fixed bottom-24 sm:bottom-6 right-5 z-40 inline-flex items-center gap-2.5 rounded-full pl-4 pr-5"
      style={{
        minHeight: 56,
        backgroundColor: "#25D366",
        color: "#0B2E17",
        border: "2.5px solid #241A12",
        boxShadow: "4px 4px 0 #241A12",
      }}
    >
      <span className="relative inline-flex items-center justify-center">
        <span
          aria-hidden
          className="bonita-zap-anel absolute inset-0 rounded-full"
          style={{ backgroundColor: "#0B2E17" }}
        />
        <IconeWhatsApp className="relative h-7 w-7" />
      </span>
      <span
        className="bonita-display hidden sm:inline"
        style={{ fontSize: 16, letterSpacing: "0.01em" }}
      >
        Encomendar
      </span>
    </a>
  )
}
